import { SerializedError, createSlice, isAnyOf } from '@reduxjs/toolkit';

import { TProductInstance } from '@lib/core/products/types';
import { IRequestParams, getProductsListData } from '@lib/core/products/utils';
import { selectAppliedAddOns } from '@lib/core/retailers/selectors/retailerLocation';
import { ApiQueries, ApiValues, TEST_RESULT_VIEW_MAX_PRODUCTS } from '@lib/core/service/consts';
import { createTypedAsyncThunk } from '@lib/core/service/createTypedAsyncThunk';
import { shuffleProductsInsideRankGroups } from '@lib/core/service/utils';

interface IFetchSituationalResultPageProducts {
  charactersIdentifiers: string[];
  params: IRequestParams;
}
export interface IKioskResultPageProducts {
  isProductsLoading: boolean;
  products: TProductInstance[][];
  error: SerializedError;
}

export interface IKioskRootState {
  resultPageProducts: IKioskResultPageProducts;
}

const initialState: IKioskRootState = {
  resultPageProducts: {
    error: null,
    isProductsLoading: false,
    products: [],
  },
};

export const fetchProducts = async (
  params: IRequestParams,
  isFeatureProductsAddon: boolean,
  uniqueResultNumber: number = TEST_RESULT_VIEW_MAX_PRODUCTS,
) => {
  params[ApiQueries.ordering] = ApiValues.orderingByRank;
  const { results: products = [] } = await getProductsListData({
    ...params,
    ...(isFeatureProductsAddon && { [ApiQueries.promotions]: ApiValues.promotionFeature }),
  });
  const featuredShuffledRankProducts = shuffleProductsInsideRankGroups(products)?.slice(0, uniqueResultNumber);

  const missedProductsQuantity = uniqueResultNumber - featuredShuffledRankProducts.length;

  if (!missedProductsQuantity || !isFeatureProductsAddon) {
    return featuredShuffledRankProducts;
  }

  const randomProductsIDs = featuredShuffledRankProducts.map(product => product.identifier);

  const { results: missedProducts = [] } = await getProductsListData({
    ...params,
  });
  const filteredMissedProducts = missedProducts.filter(product => !randomProductsIDs.includes(product.identifier));
  const missedShuffledRankedProducts = [...shuffleProductsInsideRankGroups(filteredMissedProducts)]?.slice(
    0,
    missedProductsQuantity,
  );

  return [...featuredShuffledRankProducts, ...missedShuffledRankedProducts];
};

export const fetchResultPageProducts = createTypedAsyncThunk(
  'kioskRoot/fetchResultPageProducts',
  async (params: IRequestParams, thunkAPI) => {
    const { isFeatureProductsAddon } = selectAppliedAddOns(thunkAPI.getState());

    const products = await fetchProducts(params, isFeatureProductsAddon);
    return [products];
  },
);

export const fetchSituationalResultPageProducts = createTypedAsyncThunk(
  'kioskRoot/fetchSituationalResultPageProducts',
  async ({ charactersIdentifiers, params }: IFetchSituationalResultPageProducts, thunkAPI) => {
    const { isFeatureProductsAddon } = selectAppliedAddOns(thunkAPI.getState());

    const productsPromises = charactersIdentifiers.map(characterId =>
      fetchProducts({ ...params, [ApiQueries.character]: characterId }, isFeatureProductsAddon),
    );
    const products: TProductInstance[][] = await Promise.all(productsPromises);
    return products;
  },
);

const kioskRootSlice = createSlice({
  extraReducers: builder => {
    builder
      .addMatcher(isAnyOf(fetchResultPageProducts.pending, fetchSituationalResultPageProducts.pending), state => {
        state.resultPageProducts = { ...state.resultPageProducts, isProductsLoading: true };
      })
      .addMatcher(
        isAnyOf(fetchResultPageProducts.fulfilled, fetchSituationalResultPageProducts.fulfilled),
        (state, action) => {
          state.resultPageProducts = {
            ...state.resultPageProducts,
            isProductsLoading: false,
            products: action.payload,
          };
        },
      )
      .addMatcher(
        isAnyOf(fetchResultPageProducts.rejected, fetchSituationalResultPageProducts.rejected),
        (state, action) => {
          state.resultPageProducts = {
            ...state.resultPageProducts,
            error: action.error,
            isProductsLoading: false,
          };
        },
      );
  },
  initialState,
  name: 'kioskApp',
  reducers: {
    resetResultPageProducts(state) {
      state.resultPageProducts = initialState.resultPageProducts;
    },
  },
});

export const { resetResultPageProducts } = kioskRootSlice.actions;
export default kioskRootSlice.reducer;
